import { emitEvent, getKey, uniqueId } from './util.js'

const localStorage = window.localStorage

export class Storage {
  static delete (key = undefined, persist = true, preserveKey = false) {
    if (key === undefined) {
      localStorage.clear()
    } else {
      if (!preserveKey) {
        key = Storage.key(key)
      }
      localStorage.removeItem(key)
    }

    return emitEvent(Storage.Events.Delete, { key, persist })
  }

  static get (key) {
    return key === undefined ? { ...localStorage } : localStorage.getItem(Storage.key(key))
  }

  static key (...values) {
    return getKey(Storage.Prefix, Storage.Profile.getId(), ...values.filter((v) => v !== null && v !== undefined))
  }

  static set (key, value, persist = true) {
    if (typeof key === 'object') {
      if (typeof value === 'boolean') {
        persist = value
      }

      for (const [k, v] of Object.entries(key)) {
        Storage.#set(k, v)
      }
    } else {
      Storage.#set(key, value)
    }

    emitEvent(Storage.Events.Set, { key, value, persist })
  }

  static #set (key, value) {
    localStorage.setItem(Storage.key(key), (typeof value === 'function' ? value() : value).toString())
  }

  static Key = 'storage'

  static Events = Object.freeze({
    Delete: getKey(Storage.Key, 'delete'),
    Set: getKey(Storage.Key, 'set')
  })

  static Keys = Object.freeze({
    Profile: 'profile',
    Profiles: 'profiles'
  })

  static Prefix = 'beaming'

  static Profile = class {
    id
    name

    constructor (name, id) {
      this.id = id ?? uniqueId()
      this.name = name
    }

    static get (id) {
      return Storage.Profiles.get(id ?? Storage.Profile.getId())
    }

    static getId () {
      return localStorage.getItem(getKey(Storage.Prefix, Storage.Keys.Profile)) ?? Storage.ProfilesByName.Default.id
    }
  }

  static ProfilesByName = Object.freeze({
    Default: new Storage.Profile('default', 'default'),
    Share: new Storage.Profile('share', 'share')
  })

  static ProfilesById = Object.freeze(Object.fromEntries(
    Object.values(Storage.ProfilesByName).map((profile) => [profile.id, profile])))

  static ProfileIds = Object.keys(Storage.ProfilesById)

  static Profiles = class {
    static add (name) {
      const profiles = Storage.Profiles.get()
      const profile = new Storage.Profile(name)
      profiles.push(profile)
      localStorage.setItem(getKey(Storage.Prefix, Storage.Keys.Profiles), JSON.stringify(profiles))
      return profile
    }

    static clear (id) {
      const profile = Storage.Profiles.get(id)
      if (!profile) {
        throw new Error(`Invalid profile id: ${id}`)
      }
      Object.keys(Storage.get())
        .filter((key) => key.startsWith(getKey(Storage.Prefix, id)))
        .forEach((key) => Storage.delete(key, true, true))
    }

    static get (id) {
      const def = Storage.ProfilesById[id]
      if (def) {
        return def
      }

      const profiles = JSON.parse(localStorage.getItem(getKey(Storage.Prefix, Storage.Keys.Profiles)) ?? '[]')
      return id ? profiles.find((profile) => profile.id === id) : profiles
    }

    static remove (id) {
      const profiles = Storage.Profiles.get()
      const index = profiles.findIndex((profile) => profile.id === id)
      if (index < 0) {
        throw new Error(`Invalid profile id: ${id}`)
      }

      Storage.Profiles.clear(id)

      const profile = profiles.splice(index, 1)[0]
      localStorage.setItem(getKey(Storage.Prefix, Storage.Keys.Profiles), JSON.stringify(profiles))
      return profile
    }

    static set (id) {
      const profile = Storage.Profiles.get(id)
      if (!profile) {
        throw new Error(`Invalid profile id: ${id}`)
      }

      if (profile.id === Storage.ProfilesByName.Default) {
        localStorage.removeItem(getKey(Storage.Prefix, Storage.Keys.Profile))
      } else {
        localStorage.setItem(getKey(Storage.Prefix, Storage.Keys.Profile), id)
      }

      emitEvent(Storage.Profiles.Events.Updated, { profile })
      return profile
    }

    static Events = Object.freeze({
      Update: getKey(Storage.Key, Storage.Keys.Profiles, 'update'),
      Updated: getKey(Storage.Key, Storage.Keys.Profiles, 'updated')
    })
  }
}
